/* eslint-disable react/no-array-index-key */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';
import Table from '../../components/table/Table';
import { getProjects } from '../../core/state/project/asyncActions';
import { Project, ProjectStatus } from '../../core/state/project/types';
import { RootState } from '../../core/state/state';
import Image from '../../components/image/Image';
import { BASE_URL } from '../../core/constants/api';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import TH from '../../components/table/TH/TH';
import THead from '../../components/table/THead/THead';
import Badge from '../components/badge/Badge';
import { Color } from '../../core/enums/Color';
import Button from '../../components/form/button/Button';
import ProjectActivationModal from './modals/project-activation-modal/ProjectActivationModal';
import { isReadAllRole } from '../../core/state/user/selectors';

import './ProjectManagement.scss';

const ProjectManagement: React.FC = () => {
  const { projects } = useSelector((state: RootState) => state.projects);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedProject, setSelectedProject] = React.useState<number>(0);
  const [isProjectActive, setIsProjectActive] = React.useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const isUserReader = useSelector(isReadAllRole);

  React.useEffect(() => {
    dispatch(getProjects());
  }, [dispatch]);

  const openModal = (projectId: number, isActive: boolean) => {
    setSelectedProject(projectId);
    setIsProjectActive(isActive);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedProject(0);
    setIsModalOpen(false);
  };

  const getProjectStatus = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.Active:
        return t('common.active');
      case ProjectStatus.Archived:
        return t('projectManagement.status.archived');
      default:
        return t('common.inactive');
    }
  };

  return (
    <TableWrapper>
      <div className="table__header">
        <div className="d-flex">
          <span>{t('projectManagement.title')}</span>
        </div>
      </div>
      <div className="project-management__table">
        <Table>
          <THead>
            <TR>
              <TH>{t('common.projectName')}</TH>
              <TH>{t('common.projectNumber')}</TH>
              <TH>{t('common.constructionArea')}</TH>
              <TH>{t('common.status')}</TH>
              <TH>{t('common.action')}</TH>
            </TR>
          </THead>
          <TBody>
            {projects?.map((project: Project, i) => (
              <TR key={`${i}-${project.id}`}>
                <TD className="d-flex align-items-center">
                  <Image className="project-management__project-img" src={`${BASE_URL}/${project.photo}`} alt={project.photo} />
                  <div className="font-weight-bold">{project.name}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{project.code}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{project.constructionSite}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">
                    <Badge
                      color={project.statusEnum === ProjectStatus.Active ? Color.Green : Color.Grey}
                      label={getProjectStatus(project.statusEnum)}
                    />
                  </div>
                </TD>
                {!isUserReader && project.statusEnum && (
                  <TD className="text-left">
                    <Button
                      onClick={() => openModal(project.id, project.statusEnum === ProjectStatus.Active)}
                      className="external-users__edit"
                      buttonTypes={[project.statusEnum === ProjectStatus.Active ? 'outline' : 'blue-outline']}
                    >
                      <span>
                        {t(
                          project.statusEnum === ProjectStatus.Archived || project.statusEnum === ProjectStatus.Disabled
                            ? 'common.activate'
                            : 'projectManagement.button.archive'
                        )}
                      </span>
                    </Button>
                  </TD>
                )}
              </TR>
            ))}
          </TBody>
        </Table>
        {!projects && <NoResultsPage title={t('common.noProjects')} description={t('projectListing.noProjects.text')} icon="projects" />}
      </div>
      {isModalOpen && selectedProject && (
        <ProjectActivationModal isActive={isProjectActive} projectId={selectedProject} isOpen={isModalOpen} closeModal={() => closeModal()} />
      )}
    </TableWrapper>
  );
};

export default ProjectManagement;
